import type {
  AuthTokenCreationQuery,
  RecoverPasswordCreationQuery,
  Refresh2FactorCodeQuery,
  Send2FactorValidationQuery,
} from "@/Modules/Models/Domain/Auth";
import type {
  AuthTokenApi,
  AuthTokenApiCreationQuery,
  RecoverPasswordApiCreationQuery,
  ResetPasswordApiCreationQuery,
  RefreshTokenApi,
  RefreshTokenQuery,
  SSOAuthApi,
  SSOAuthQuery,
} from "@/Modules/Models/Api/Auth";

import { isValidString } from "@/Utils/String";

import { post2, patch2 } from "./utils/FunctionalTherapyApi";

let logoutRequest: Promise<void> | null = null;

export const authSSOToken = (token: string): Promise<SSOAuthApi> => {
  return post2<SSOAuthQuery, SSOAuthApi>("/v1.0/auth/sso/tokens", { sso_token: token }, undefined, {
    withCredentials: true,
  });
};

export const login = (authTokenCreationQuery: AuthTokenCreationQuery): Promise<AuthTokenApi> => {
  return post2<AuthTokenApiCreationQuery, AuthTokenApi>(
    "/v2.0/users/login",
    authTokenCreationQuery,
    undefined,
    { withCredentials: true }
  );
};

export const send2FCode = async (query: Send2FactorValidationQuery): Promise<AuthTokenApi> => {
  return post2<Send2FactorValidationQuery, AuthTokenApi>("/v1.0/auth/two-factor", query, undefined, {
    withCredentials: true,
  });
};

export const refreshCode2Factor = async (
  query: Refresh2FactorCodeQuery
): Promise<Pick<AuthTokenApi, "hash_2F">> => {
  return patch2<Refresh2FactorCodeQuery, AuthTokenApi>("/v1.0/auth/two-factor", query);
};

export const logout = (forcedToken?: string): Promise<void> => {
  if (!logoutRequest) {
    const headers = isValidString(forcedToken) ? { Authorization: `Bearer ${forcedToken}` } : undefined;

    logoutRequest = post2<void, void>("v1.0/users/logout", undefined, headers, {
      withCredentials: true,
    }).finally(() => {
      logoutRequest = null;
    });
  }

  return logoutRequest;
};

export const recoverPassword = async (
  recoverPasswordApiCreationQuery: RecoverPasswordApiCreationQuery
): Promise<void> => {
  await post2<RecoverPasswordCreationQuery, void>(
    "/v1.0/passwords/recovery",
    recoverPasswordApiCreationQuery
  );
};

export const resetPassword = async (
  resetPasswordApiCreationQuery: ResetPasswordApiCreationQuery
): Promise<void> => {
  await post2<ResetPasswordApiCreationQuery, void>("/v1.0/passwords/reset", resetPasswordApiCreationQuery);
};

export const refreshToken = async (token: string): Promise<RefreshTokenApi> => {
  return post2<RefreshTokenQuery, RefreshTokenApi>("/v1.0/auth/refresh", { token }, undefined, {
    withCredentials: true,
  });
};
